import { type PropsWithChildren, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useThrottledCallback } from 'use-debounce';
import { useStore } from 'zustand/react';
import type { ModalAction } from '@/components/common/modal/modal.type';
import { ConversationBottomBar } from '@/components/conversations/conversation-bottom-bar';
import type { ConversationInputForwardedProps } from '@/components/conversations/conversation-form';
import type { ContentInteractionType } from '@/components/conversations/conversation-list/message-list-item/message-content/content-common-props';
import ConversationMenuModal from '@/components/conversations/conversation-menu-modal';
import useAddCollection from '@/hooks/collections/use-add-collection';
import useFriends from '@/hooks/contacts/use-friends';
import { HandlerContext, StoreContext } from '@/hooks/conversations/use-conversation-store';
import useDeleteMessages from '@/hooks/conversations/use-delete-messages';
import useMessageWebSocketDispatch from '@/hooks/conversations/use-message-web-socket-dispatch';
import useBanGroupMember from '@/hooks/groups/use-ban-group-member';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useKickGroupMember from '@/hooks/groups/use-kick-group-member';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { contactMessageScheme } from '@/models/messages/message-type';
import ROUTES from '@/routes';
import type { ConversationStoreType } from '@/stores/conversation';
import { makePrompt } from '@/stores/prompter';
import { isMedia } from '@/utils/check-messages-status';
import copyTextToClipboard from '@/utils/copy-text';
import { isGroupMessage } from '@/utils/message';

interface Props {
  layout?: 'full' | 'mini';
  type: ConversationType;
  id: number;
  store: ConversationStoreType;
}

export default function ConversationProviders({
  layout = 'full',
  type,
  id,
  store,
  children,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [position, setPosition] = useState<[number, number] | null>(null);

  const bottomBarRef = useRef<ConversationInputForwardedProps>(null);
  const menuRef = useRef<ModalAction>(null);

  const { retry, submit, isLoading: isSubmitting } = useMessageWebSocketDispatch(type, id);
  const { mutateAsync: addCollection } = useAddCollection();
  const { friends } = useFriends();

  const { checkCanMessage, data: group, canMessage, isChannel } = useGroupInfo(type === 'session' ? null : id);
  const { deleteMessage } = useDeleteMessages(type, id);
  const { mutateAsync: banMember } = useBanGroupMember(type === 'group' ? id : undefined);
  const { mutateAsync: kickOutMember } = useKickGroupMember(type === 'group' ? id : undefined);

  const { setMultiselectAction } = useStore(store);

  const handleCheckStatus = useThrottledCallback(checkCanMessage, 3000);

  const handleInteraction = async (action: ContentInteractionType, eventPosition?: [number, number]) => {
    setPosition(null);

    const message = store.getState().selectedMessages.at(0);
    if (!message) return;

    switch (action) {
      case 'contact':
        try {
          const contact = contactMessageScheme.parse(JSON.parse(message.Message));
          if (friends[contact.UserID]) {
            navigate(
              ROUTES.CONTACT.USERS.BY_ID.$buildPath({
                params: {
                  userId: contact.UserID,
                },
              }),
            );
            return;
          }
          const userCodePath = ROUTES.CONTACT.REQUEST.USER.$buildPath({
            searchParams: {
              id: contact.UserID,
              key: contact.VChatID as string,
              ...(isGroupMessage(message) ? { groupId: message.GroupID } : { sessionId: message.ChatSessionID }),
              source: 'contact',
            },
          });

          navigate(userCodePath);
        } catch (e) {
          console.error(e);
          return;
        }
        break;
      case 'quote':
        if (message.Status) {
          bottomBarRef.current?.quote(message);
        }
        break;
      case 'retry':
        retry(message);
        break;
      case 'mention':
        bottomBarRef.current?.mention(message);
        break;
      case 'mute':
        if (type === 'group') {
          makePrompt({
            key: 'mute-member',
            title: t('conversation.setting.group.ban-confirm'),
            resolveButtonLabel: t('conversation.setting.group.ban'),
            promise: () => banMember({ userId: message.UserID, ban: 'ban' }),
          }).catch(console.warn);
        }
        break;
      case 'unmute':
        if (type === 'group') banMember({ userId: message.UserID, ban: 'unban' }).catch(console.warn);
        break;
      case 'kickOut':
      case 'kickOutAndPurge':
        if (type === 'group')
          makePrompt({
            key: 'kick-member',
            title: t('conversation.kick-out-confirm-message'),
            resolveButtonLabel:
              action === 'kickOut' ? t('conversation.kick-out') : t('conversation.kick-out-and-purge'),
            promise: () => kickOutMember({ userId: message.UserID, purgeMessages: action === 'kickOutAndPurge' }),
          }).catch(console.warn);
        break;
      case 'recall':
        deleteMessage(message);
        break;
      case 'delete':
        setMultiselectAction('delete');
        break;
      case 'save':
        {
          let smallPicturePath = '';
          let content = message.Message;

          if (
            message.MsgType === ConversationMessageContentType.Image ||
            message.MsgType === ConversationMessageContentType.Video
          ) {
            smallPicturePath = message.Message;
          }

          if (message.MsgType === ConversationMessageContentType.File) {
            const [fileURL, fileName, fileSize] = message.Message.split('|');
            content = `${fileName}|${fileSize}`;
            smallPicturePath = fileURL ?? '';
          }

          const isGroupSession = type === 'group';
          makePrompt({
            key: 'favorites-add',
            title: t('conversation.favorites-add-confirm-message'),
            resolveButtonLabel: t('conversation.favorites-add'),
            promise: () =>
              addCollection({
                FromID: (isGroupSession ? id : message.UserID) ?? 0,
                FromName:
                  (isGroupSession ? group?.GroupData?.GroupName : message.UserDisplayName || message.UserNickName) ??
                  '',
                FromType: isGroupSession ? 'group' : 'friend',
                Content: content,
                Type: message.MsgType,
                SmallPicturePath: smallPicturePath,
              }),
          }).catch(console.warn);
        }
        break;
      case 'preview':
        {
          const isGroup = type === 'group';
          const messageIdPath = isGroup
            ? ROUTES.MESSAGES.GROUPS.MESSAGE_BY_ID.$buildPath({
                params: { id, messageId: message.ID },
              })
            : ROUTES.MESSAGES.SESSIONS.MESSAGE_BY_ID.$buildPath({
                params: { id, messageId: message.ID },
              });
          const mediaPath = isGroup
            ? ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID.$buildPath({ params: { id, messageId: message.ID } })
            : ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID.$buildPath({ params: { id, messageId: message.ID } });
          const path = isMedia(message) ? mediaPath : messageIdPath;

          navigate(path, {
            preventScrollReset: true,
            state: { from: 'message' },
          });
        }
        break;
      case 'report':
        navigate(
          (type === 'session' ? ROUTES.MESSAGES.SESSIONS : ROUTES.MESSAGES.GROUPS).REPORT.$buildPath({
            params: {
              id: id,
            },
          }),
        );
        break;
      case 'forward':
        setMultiselectAction('forward');
        break;
      case 'copy':
        if (message.MsgType === ConversationMessageContentType.Text) {
          copyTextToClipboard(message.Message).catch(console.warn);
        }
        break;
      case 'contextmenu':
        if (eventPosition) setPosition(eventPosition);
        document.querySelector<HTMLTextAreaElement>('#conversation-textarea')?.blur();
        menuRef.current?.open();
        break;
      case 'multiselect':
        setMultiselectAction('default');
        break;
      default:
        break;
    }
  };

  return (
    <StoreContext.Provider value={store}>
      <HandlerContext.Provider
        value={{
          handleInteraction,
          handleSubmit: submit,
          handleCheckStatus: async () => (type === 'group' && handleCheckStatus?.()) ?? false,
        }}>
        <div className='relative flex w-full flex-1 flex-col justify-end overflow-hidden'>{children}</div>

        {!isChannel && (
          <aside className='inset-x-0'>
            <ConversationBottomBar
              ref={bottomBarRef}
              layout={layout}
              disabled={type === 'group' && !canMessage}
              isLoading={isSubmitting}
            />
          </aside>
        )}

        <ConversationMenuModal
          ref={menuRef}
          position={position}
          onClose={() => {
            menuRef.current?.close();
          }}
        />
      </HandlerContext.Provider>
    </StoreContext.Provider>
  );
}
